import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  HbrButton,
  HbrCard,
  HbrDropdown,
  HbrIcon,
  HbrInput,
  HbrMenu,
  HbrMenuItem,
  HbrSpinner,
  HbrTab,
  HbrTabGroup,
  HbrTag,
} from '@harbor/elements-react-standalone';
import {
  arrowRight,
  checkCircle,
  clock,
  funnelSimple,
  magnifyingGlass,
  mapTrifold,
  sort,
} from '@harbor/elements/icons';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../../../support/queries';
import { RootState } from '../../../support/store';
import JourneyMapAssignmentsManagerPage from './JourneyMapAssignmentsManagerPage';

type JourneyMapRecord = {
  id: number | string;
  name?: string;
  title?: string;
  description?: string;
  status?: string;
  estimated_duration_minutes?: number | null;
  item_count?: number | null;
  created?: string;
  last_updated?: string;
  journey_type?: string | number | null;
  type?: string | number | null;
  is_assigned?: boolean | null;
  assigned_to_user?: boolean | null;
};

type JourneyTypeRecord = {
  id: number | string;
  name?: string;
  title?: string;
  label?: string;
};

type JourneyMapAssignmentRecord = {
  id: number | string;
  journey_map?: number | string | null;
  user?: number | string | null;
  status?: number | string | null;
  status_name?: string | null;
  priority?: number | null;
  due_date?: string | null;
  progress_percent?: number | string | null;
  source_type?: string | null;
  is_active?: boolean | null;
  assigned_date?: string | null;
  created?: string | null;
  last_updated?: string | null;
  assignment_bucket?: 'assigned' | 'completed' | 'inactive' | string;
  is_completed?: boolean;
  is_assigned_state?: boolean;
};

type SortMode =
  | 'title-asc'
  | 'title-desc'
  | 'updated-desc'
  | 'duration-asc'
  | 'steps-desc';

type ViewMode = 'assigned' | 'available' | 'completed' | 'assign-journeys';

const loadJourneyMaps = async (): Promise<JourneyMapRecord[]> => {
  const data = await fetchItems('db/journey-map/');
  return Array.isArray(data) ? data : [];
};

const loadJourneyTypes = async (): Promise<JourneyTypeRecord[]> => {
  try {
    const data = await fetchItems('db/journey-map-type/', {}, { includePageSize: false });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

const loadMyAssignments = async (userId: number | string): Promise<JourneyMapAssignmentRecord[]> => {
  if (!userId) return [];
  const data = await fetchItems(
    'db/journey-map-assignment/',
    { user: userId },
    { includePageSize: false }
  );
  return Array.isArray(data) ? data : [];
};

const clamp2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const clamp3: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const getJourneyTitle = (journeyMap: JourneyMapRecord) =>
  journeyMap?.name || journeyMap?.title || 'Untitled Journey Map';

const getJourneyDescription = (journeyMap: JourneyMapRecord) =>
  journeyMap?.description || 'No description available.';

const getJourneyTypeId = (journeyMap: JourneyMapRecord) =>
  journeyMap?.journey_type ?? journeyMap?.type ?? null;

const sortJourneyMaps = (rows: JourneyMapRecord[], sortMode: SortMode) => {
  const cloned = [...rows];

  cloned.sort((a, b) => {
    const titleA = getJourneyTitle(a).toLowerCase();
    const titleB = getJourneyTitle(b).toLowerCase();

    switch (sortMode) {
      case 'title-asc':
        return titleA.localeCompare(titleB);

      case 'title-desc':
        return titleB.localeCompare(titleA);

      case 'updated-desc': {
        const aTs = Date.parse(String(a?.last_updated || a?.created || ''));
        const bTs = Date.parse(String(b?.last_updated || b?.created || ''));
        return (Number.isNaN(bTs) ? 0 : bTs) - (Number.isNaN(aTs) ? 0 : aTs);
      }

      case 'duration-asc':
        return Number(a?.estimated_duration_minutes || 0) - Number(b?.estimated_duration_minutes || 0);

      case 'steps-desc':
        return Number(b?.item_count || 0) - Number(a?.item_count || 0);

      default:
        return 0;
    }
  });

  return cloned;
};

const isCompletedAssignment = (assignment?: JourneyMapAssignmentRecord | null) => {
  if (!assignment) return false;

  if (typeof assignment?.is_completed === 'boolean') {
    return assignment.is_completed;
  }

  if (assignment?.assignment_bucket) {
    return String(assignment.assignment_bucket).trim().toLowerCase() === 'completed';
  }

  const statusName = String(assignment?.status_name || '').trim().toLowerCase();
  const progressPercent = Number(assignment?.progress_percent || 0);
  return statusName === 'completed' || progressPercent >= 100;
};

const isAssignedAssignment = (assignment?: JourneyMapAssignmentRecord | null) => {
  if (!assignment) return false;

  if (isCompletedAssignment(assignment)) {
    return false;
  }

  if (typeof assignment?.is_assigned_state === 'boolean') {
    return assignment.is_assigned_state;
  }

  if (assignment?.assignment_bucket) {
    return String(assignment.assignment_bucket).trim().toLowerCase() === 'assigned';
  }

  if (assignment?.is_active != null) {
    return Boolean(assignment.is_active);
  }

  return true;
};

const getAssignmentRank = (assignment?: JourneyMapAssignmentRecord | null) => {
  if (!assignment) return 0;
  if (isCompletedAssignment(assignment)) return 3;
  if (isAssignedAssignment(assignment)) return 2;
  return 1;
};

type JourneyCardProps = {
  journeyMap: JourneyMapRecord;
  featured?: boolean;
  state: 'assigned' | 'completed' | 'available';
  journeyTypeLabel: string;
  onOpen: (id: string | number) => void;
  assignment?: JourneyMapAssignmentRecord | null;
};

const JourneyCard: React.FC<JourneyCardProps> = ({
                                                   journeyMap,
                                                   featured = false,
                                                   state,
                                                   journeyTypeLabel,
                                                   onOpen,
                                                   assignment,
                                                 }) => {
  const title = getJourneyTitle(journeyMap);
  const description = getJourneyDescription(journeyMap);
  const duration = journeyMap?.estimated_duration_minutes;
  const itemCount = journeyMap?.item_count;

  const sentiment =
    state === 'completed' ? 'success' : state === 'assigned' ? 'info' : 'neutral';

  const stateLabel =
    state === 'completed' ? 'Completed' : state === 'assigned' ? 'Assigned' : 'Available';

  const progressPercent =
    assignment?.progress_percent != null ? Number(assignment.progress_percent) : null;

  return (
    <HbrCard
      selectable
      role="button"
      sentiment={sentiment}
      onClick={() => onOpen(journeyMap.id)}
      style={
        {
          '--width': '100%',
          '--min-height': featured ? '260px' : '220px',
          '--border-radius': '10px',
          '--box-shadow': '--hbr-shadow-xs',
        } as React.CSSProperties
      }
    >
      <div slot="header" className="hbr-css__layout-col-sm" style={{ minWidth: 0 }}>
        <div className="hbr-css__layout-row-sm hbr-css__layout-justify-between hbr-css__layout-align-start">
          <div className="hbr-css__layout-col-3xs" style={{ minWidth: 0, flex: 1 }}>
            <div
              className={featured ? 'hbr-css__text-heading-lg-bold' : 'hbr-css__text-heading-md-bold'}
              style={{
                color: 'var(--hbr-color-neutral-text)',
                ...(featured ? clamp3 : clamp2),
              }}
            >
              {title}
            </div>
          </div>

          <div
            style={{
              width: featured ? 40 : 32,
              height: featured ? 40 : 32,
              borderRadius: 999,
              display: 'grid',
              placeItems: 'center',
              backgroundColor:
                state === 'completed'
                  ? 'var(--hbr-color-success-bg)'
                  : state === 'assigned'
                    ? 'var(--hbr-color-info-bg)'
                    : 'var(--hbr-color-neutral-bg-medium)',
              color:
                state === 'completed'
                  ? 'var(--hbr-color-success)'
                  : state === 'assigned'
                    ? 'var(--hbr-color-info)'
                    : 'var(--hbr-color-neutral-text)',
              flexShrink: 0,
            }}
          >
            <HbrIcon asset={arrowRight} style={{ fontSize: featured ? '18px' : '16px' }} />
          </div>
        </div>

        <div
          className="hbr-css__text-secondary-sm"
          style={{
            color: 'var(--hbr-color-neutral-text-weak)',
            minHeight: featured ? '72px' : '60px',
            ...clamp3,
          }}
        >
          {description}
        </div>
      </div>

      <div className="hbr-css__layout-col-sm" style={{ height: '100%', justifyContent: 'space-between' }}>
        <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
          <HbrTag size="small">{journeyTypeLabel}</HbrTag>

          {itemCount != null ? <HbrTag size="small">{itemCount} steps</HbrTag> : null}

          {duration ? (
            <HbrTag size="small">
              <span className="hbr-css__layout-row-3xs hbr-css__layout-align-center">
                <HbrIcon asset={clock} />
                {duration} min
              </span>
            </HbrTag>
          ) : null}

          <HbrTag size="small">
            <span className="hbr-css__layout-row-3xs hbr-css__layout-align-center">
              <HbrIcon asset={checkCircle} />
              {stateLabel}
            </span>
          </HbrTag>

          {progressPercent != null && state !== 'available' ? (
            <HbrTag size="small">{Math.round(progressPercent)}% complete</HbrTag>
          ) : null}

          {featured ? <HbrTag size="small">Featured</HbrTag> : null}
        </div>

        <div
          className="hbr-css__text-secondary-xs"
          style={{
            color: 'var(--hbr-color-neutral-text-weak)',
            marginTop: '8px',
          }}
        >
          Open this journey to launch content, complete tasks, and progress through the learning path.
        </div>
      </div>
    </HbrCard>
  );
};

type JourneySectionProps = {
  title: string;
  subtitle: string;
  journeys: JourneyMapRecord[];
  getJourneyTypeLabel: (journey: JourneyMapRecord) => string;
  getAssignmentForJourney?: (journeyId: string | number) => JourneyMapAssignmentRecord | null;
  onOpen: (id: string | number) => void;
  state: 'assigned' | 'completed' | 'available';
  featuredFirst?: boolean;
  emptyMessage?: string;
};

const JourneySection: React.FC<JourneySectionProps> = ({
                                                         title,
                                                         subtitle,
                                                         journeys,
                                                         getJourneyTypeLabel,
                                                         getAssignmentForJourney,
                                                         onOpen,
                                                         state,
                                                         featuredFirst = false,
                                                         emptyMessage,
                                                       }) => {
  if (!journeys.length) {
    return (
      <div className="hbr-css__layout-col-md">
        <div className="hbr-css__layout-col-3xs">
          <div className="hbr-css__text-heading-md-bold">{title}</div>
          <div className="hbr-css__text-secondary-sm">{subtitle}</div>
        </div>

        <HbrCard
          style={
            {
              '--box-shadow': 'none',
              '--border-width': '2px',
              '--border-color': 'var(--hbr-color-neutral-border-weak)',
              backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
            } as React.CSSProperties
          }
        >
          <div className="hbr-css__text-secondary-sm">
            {emptyMessage || 'No journey maps to show.'}
          </div>
        </HbrCard>
      </div>
    );
  }

  const featuredJourney = featuredFirst ? journeys[0] : null;
  const remainingJourneys = featuredFirst ? journeys.slice(1) : journeys;

  return (
    <div className="hbr-css__layout-col-md">
      <div className="hbr-css__layout-col-3xs">
        <div className="hbr-css__text-heading-md-bold">{title}</div>
        <div className="hbr-css__text-secondary-sm">{subtitle}</div>
      </div>

      {featuredJourney ? (
        <JourneyCard
          journeyMap={featuredJourney}
          featured
          state={state}
          journeyTypeLabel={getJourneyTypeLabel(featuredJourney)}
          assignment={getAssignmentForJourney?.(featuredJourney.id) ?? null}
          onOpen={onOpen}
        />
      ) : null}

      {remainingJourneys.length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
            alignItems: 'stretch',
          }}
        >
          {remainingJourneys.map((journeyMap) => (
            <JourneyCard
              key={String(journeyMap.id)}
              journeyMap={journeyMap}
              state={state}
              journeyTypeLabel={getJourneyTypeLabel(journeyMap)}
              assignment={getAssignmentForJourney?.(journeyMap.id) ?? null}
              onOpen={onOpen}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const JourneyMapListPage: React.FC = () => {
  const navigate = useNavigate();

  const authz = useSelector((state: RootState) => state.user?.authz);
  const isManager = useSelector((state: RootState) => state.user?.userData?.isManager);
  const currentUserId = useSelector((state: RootState) => state.user?.userData?.id);

  const permSet = useMemo(() => new Set<string>(authz?.permissions || []), [authz?.permissions]);

  const isJourneyMapAdmin =
    permSet.has('journey-map-admin.view') ||
    permSet.has('journey-map.assign');

  const canAssignJourneys =
    !!isManager ||
    isJourneyMapAdmin ||
    permSet.has('journey-map-manager.view');

  const assignmentTabLabel = isJourneyMapAdmin ? 'Assign Journeys' : 'Assign to Reports';

  const [viewMode, setViewMode] = useState<ViewMode>('assigned');
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('updated-desc');
  const [selectedJourneyType, setSelectedJourneyType] = useState<string>('All');

  useEffect(() => {
    if (viewMode === 'assign-journeys' && !canAssignJourneys) {
      setViewMode('assigned');
    }
  }, [viewMode, canAssignJourneys]);

  const {
    data: journeyMapsData,
    isLoading: isLoadingJourneyMaps,
    error: journeyMapsError,
  } = useQuery({
    queryKey: ['journey-maps'],
    queryFn: loadJourneyMaps,
    ...{
      staleTime: 60_000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });

  const { data: journeyTypesData = [] } = useQuery({
    queryKey: ['journey-map-types'],
    queryFn: loadJourneyTypes,
    ...{ staleTime: 60_000 },
  });

  const {
    data: myAssignmentsData = [],
    isLoading: isLoadingAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: ['my-journey-map-assignments', currentUserId],
    queryFn: async () => await loadMyAssignments(currentUserId as string | number),
    ...{
        enabled: !!currentUserId,
        staleTime: 60_000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
      },
  });

  const journeyMaps = useMemo(
    () => (Array.isArray(journeyMapsData) ? journeyMapsData : []),
    [journeyMapsData]
  );

  const myAssignments = useMemo(
    () => (Array.isArray(myAssignmentsData) ? myAssignmentsData : []),
    [myAssignmentsData]
  );

  const journeyTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    journeyTypesData.forEach((t) => {
      const id = String(t?.id);
      const label = t?.name || t?.title || t?.label || `Type ${id}`;
      map.set(id, label);
    });
    return map;
  }, [journeyTypesData]);

  const myAssignmentsByJourneyId = useMemo(() => {
    const map = new Map<string, JourneyMapAssignmentRecord>();

    myAssignments.forEach((assignment) => {
      if (assignment?.journey_map == null) return;

      const key = String(assignment.journey_map);
      const existing = map.get(key);

      if (!existing) {
        map.set(key, assignment);
        return;
      }

      const existingRank = getAssignmentRank(existing);
      const nextRank = getAssignmentRank(assignment);

      if (nextRank > existingRank) {
        map.set(key, assignment);
        return;
      }

      if (nextRank === existingRank) {
        const existingTs = Date.parse(String(existing?.last_updated || existing?.created || ''));
        const nextTs = Date.parse(String(assignment?.last_updated || assignment?.created || ''));

        const safeExistingTs = Number.isNaN(existingTs) ? 0 : existingTs;
        const safeNextTs = Number.isNaN(nextTs) ? 0 : nextTs;

        if (safeNextTs > safeExistingTs) {
          map.set(key, assignment);
        }
      }
    });

    return map;
  }, [myAssignments]);

  const getAssignmentForJourney = (journeyId: string | number) =>
    myAssignmentsByJourneyId.get(String(journeyId)) ?? null;

  const getJourneyTypeLabel = (journey: JourneyMapRecord) => {
    const rawType = getJourneyTypeId(journey);
    if (rawType == null || rawType === '') return 'General';

    const rawTypeString = String(rawType);

    if (journeyTypeMap.has(rawTypeString)) {
      return journeyTypeMap.get(rawTypeString)!;
    }

    if (isNaN(Number(rawTypeString))) {
      return rawTypeString;
    }

    return `Type ${rawTypeString}`;
  };

  const journeyTypes = useMemo(() => {
    const values = Array.from(
      new Set(journeyMaps.map((j) => getJourneyTypeLabel(j)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    return ['All', ...values];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyMaps, journeyTypeMap]);

  const filteredJourneyMaps = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filtered = journeyMaps.filter((j) => {
      const title = getJourneyTitle(j).toLowerCase();
      const description = getJourneyDescription(j).toLowerCase();
      const typeLabel = getJourneyTypeLabel(j);

      const matchesSearch = !q || title.includes(q) || description.includes(q);
      const matchesType = selectedJourneyType === 'All' || typeLabel === selectedJourneyType;

      return matchesSearch && matchesType;
    });

    return sortJourneyMaps(filtered, sortMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyMaps, search, sortMode, selectedJourneyType, journeyTypeMap]);

  const assignedJourneys = useMemo(
    () =>
      filteredJourneyMaps.filter((journey) => {
        const assignment = myAssignmentsByJourneyId.get(String(journey.id));
        return isAssignedAssignment(assignment) && !isCompletedAssignment(assignment);
      }),
    [filteredJourneyMaps, myAssignmentsByJourneyId]
  );

  const completedJourneys = useMemo(
    () =>
      filteredJourneyMaps.filter((journey) => {
        const assignment = myAssignmentsByJourneyId.get(String(journey.id));
        return isCompletedAssignment(assignment);
      }),
    [filteredJourneyMaps, myAssignmentsByJourneyId]
  );

  const availableJourneys = useMemo(
    () =>
      filteredJourneyMaps.filter((journey) => {
        const assignment = myAssignmentsByJourneyId.get(String(journey.id));
        return !isAssignedAssignment(assignment) && !isCompletedAssignment(assignment);
      }),
    [filteredJourneyMaps, myAssignmentsByJourneyId]
  );

  const handleOpen = (id: string | number) => {
    navigate(`/enablement/journey-map/${id}`);
  };

  const loading = isLoadingJourneyMaps || (!!currentUserId && isLoadingAssignments);
  const error = journeyMapsError || assignmentsError;

  const selectedViewTitle =
    viewMode === 'assigned'
      ? 'Assigned'
      : viewMode === 'completed'
        ? 'Completed'
        : viewMode === 'available'
          ? 'Available'
          : assignmentTabLabel;

  return (
    <div
      className="hbr-css__layout-col-lg"
      style={{
        padding: '24px',
        maxWidth: '1440px',
        margin: '0 auto',
      }}
    >
      <HbrCard
        container
        style={
          {
            '--border-radius': '12px',
            background:
              'linear-gradient(135deg, var(--hbr-color-info-bg) 0%, var(--hbr-color-neutral-bg) 100%)',
          } as React.CSSProperties
        }
      >
        <div
          className="hbr-css__layout-row-md hbr-css__layout-justify-between hbr-css__layout-align-center"
          style={{ gap: '24px', flexWrap: 'wrap' }}
        >
          <div className="hbr-css__layout-col-sm" style={{ minWidth: 0, flex: '1 1 560px' }}>
            <div className="hbr-css__layout-row-sm hbr-css__layout-align-center">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'var(--hbr-color-info)',
                  color: 'var(--hbr-color-neutral-vivid-text)',
                  flexShrink: 0,
                }}
              >
                <HbrIcon asset={mapTrifold} style={{ fontSize: '26px' }} />
              </div>

              <div className="hbr-css__layout-col-3xs" style={{ minWidth: 0 }}>
                <h1
                  className="hbr-css__text-heading-lg-bold hbr-css__margin-none"
                  style={{ color: 'var(--hbr-color-neutral-text)' }}
                >
                  Journey Maps
                </h1>
                <div
                  className="hbr-css__text-secondary-sm"
                  style={{ color: 'var(--hbr-color-neutral-text-weak)' }}
                >
                  Explore assigned, completed, and available journeys, then launch the learning path that fits your role and goals.
                </div>
              </div>
            </div>
          </div>

          <div
            className="hbr-css__layout-row-sm hbr-css__layout-wrap"
            style={{ alignItems: 'center' }}
          >
            <HbrTag size="small">{journeyMaps.length} total journeys</HbrTag>
            <HbrTag size="small">{assignedJourneys.length} assigned</HbrTag>
            <HbrTag size="small">{completedJourneys.length} completed</HbrTag>
            <HbrTag size="small">{availableJourneys.length} available</HbrTag>
          </div>
        </div>
      </HbrCard>

      <HbrCard
        style={
          {
            '--box-shadow': 'none',
            '--border-width': '2px',
            '--border-color': 'var(--hbr-color-neutral-border-weak)',
          } as React.CSSProperties
        }
      >
        <HbrTabGroup>
          <HbrTab
            slot="nav"
            panel="assigned"
            active={viewMode === 'assigned'}
            onHbr-click={() => setViewMode('assigned')}
          >
            Assigned
          </HbrTab>

          <HbrTab
            slot="nav"
            panel="available"
            active={viewMode === 'available'}
            onHbr-click={() => setViewMode('available')}
          >
            Available
          </HbrTab>

          <HbrTab
            slot="nav"
            panel="completed"
            active={viewMode === 'completed'}
            onHbr-click={() => setViewMode('completed')}
          >
            Completed
          </HbrTab>

          {canAssignJourneys ? (
            <HbrTab
              slot="nav"
              panel="assign-journeys"
              active={viewMode === 'assign-journeys'}
              onHbr-click={() => setViewMode('assign-journeys')}
            >
              {assignmentTabLabel}
            </HbrTab>
          ) : null}
        </HbrTabGroup>
      </HbrCard>

      {viewMode === 'assign-journeys' && canAssignJourneys ? (
        <JourneyMapAssignmentsManagerPage
          journeys={journeyMaps}
          getJourneyTypeLabel={getJourneyTypeLabel}
        />
      ) : (
        <>
          <HbrCard
            style={
              {
                '--box-shadow': 'none',
                '--border-width': '2px',
                '--border-color': 'var(--hbr-color-neutral-border-weak)',
                '--border-radius': '10px',
              } as React.CSSProperties
            }
          >
            <div
              className="hbr-css__layout-row-md hbr-css__layout-justify-between hbr-css__layout-align-end"
              style={{ gap: '16px', flexWrap: 'wrap' }}
            >
              <div className="hbr-css__layout-col-sm" style={{ flex: '1 1 420px', minWidth: 0 }}>
                <div className="hbr-css__text-heading-md-bold">{selectedViewTitle}</div>
                <div className="hbr-css__text-secondary-sm">
                  Search, sort, or filter by journey type.
                </div>
              </div>

              <div
                className="hbr-css__layout-row-sm hbr-css__layout-wrap"
                style={{ alignItems: 'center', flex: '1 1 520px', justifyContent: 'flex-end' }}
              >
                <div style={{ minWidth: 260, flex: '1 1 260px', maxWidth: 380 }}>
                  <HbrInput
                    value={search}
                    placeholder="Search journey maps"
                    onHbr-input={(e: any) =>
                      setSearch(String(e?.target?.value ?? e?.detail?.value ?? ''))
                    }
                  >
                    <HbrIcon slot="prefix" asset={magnifyingGlass} />
                  </HbrInput>
                </div>

                <HbrDropdown hoist menu distance={8}>
                  <HbrButton slot="trigger" variant="outline">
                    <HbrIcon slot="prefix" asset={funnelSimple} />
                    Type: {selectedJourneyType}
                  </HbrButton>
                  <HbrMenu>
                    {journeyTypes.map((type) => (
                      <HbrMenuItem key={type} onClick={() => setSelectedJourneyType(type)}>
                        {type}
                      </HbrMenuItem>
                    ))}
                  </HbrMenu>
                </HbrDropdown>

                <HbrDropdown hoist menu distance={8}>
                  <HbrButton slot="trigger" variant="outline">
                    <HbrIcon slot="prefix" asset={sort} />
                    Sort
                  </HbrButton>
                  <HbrMenu>
                    <HbrMenuItem onClick={() => setSortMode('updated-desc')}>Recently Updated</HbrMenuItem>
                    <HbrMenuItem onClick={() => setSortMode('title-asc')}>Title A–Z</HbrMenuItem>
                    <HbrMenuItem onClick={() => setSortMode('title-desc')}>Title Z–A</HbrMenuItem>
                    <HbrMenuItem onClick={() => setSortMode('duration-asc')}>Shortest Duration</HbrMenuItem>
                    <HbrMenuItem onClick={() => setSortMode('steps-desc')}>Most Steps</HbrMenuItem>
                  </HbrMenu>
                </HbrDropdown>
              </div>
            </div>
          </HbrCard>

          {loading ? (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-neutral-border-weak)',
                } as React.CSSProperties
              }
            >
              <div
                style={{
                  minHeight: 280,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <HbrSpinner size="large">
                  <div slot="label">Loading journey maps...</div>
                </HbrSpinner>
              </div>
            </HbrCard>
          ) : error ? (
            <HbrCard
              sentiment="danger"
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-danger-border)',
                  backgroundColor: 'var(--hbr-color-danger-bg)',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-col-3xs">
                <div className="hbr-css__text-primary-sm-semibold">Unable to load journey maps</div>
                <div className="hbr-css__text-secondary-sm">
                  Please try again later or refresh the page.
                </div>
              </div>
            </HbrCard>
          ) : filteredJourneyMaps.length === 0 ? (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                  '--border-color': 'var(--hbr-color-neutral-border-weak)',
                  backgroundColor: 'var(--hbr-color-neutral-bg-medium)',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-col-3xs">
                <div className="hbr-css__text-primary-sm-semibold">No matching journey maps</div>
                <div className="hbr-css__text-secondary-sm">
                  Try adjusting your search or journey type filter.
                </div>
              </div>
            </HbrCard>
          ) : (
            <>
              {viewMode === 'assigned' ? (
                <JourneySection
                  title="Assigned"
                  subtitle="Journeys explicitly assigned to you and still in progress."
                  journeys={assignedJourneys}
                  getJourneyTypeLabel={getJourneyTypeLabel}
                  getAssignmentForJourney={getAssignmentForJourney}
                  onOpen={handleOpen}
                  state="assigned"
                  featuredFirst
                  emptyMessage="You currently have no assignments."
                />
              ) : null}

              {viewMode === 'completed' ? (
                <JourneySection
                  title="Completed"
                  subtitle="Journeys you have completed."
                  journeys={completedJourneys}
                  getJourneyTypeLabel={getJourneyTypeLabel}
                  getAssignmentForJourney={getAssignmentForJourney}
                  onOpen={handleOpen}
                  state="completed"
                  emptyMessage="You have not completed any journey maps yet."
                />
              ) : null}

              {viewMode === 'available' ? (
                <JourneySection
                  title="Available"
                  subtitle="Journeys available for you to start or rejoin."
                  journeys={availableJourneys}
                  getJourneyTypeLabel={getJourneyTypeLabel}
                  getAssignmentForJourney={getAssignmentForJourney}
                  onOpen={handleOpen}
                  state="available"
                  emptyMessage="No available journey maps match your filters."
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default JourneyMapListPage;